import { Dayjs } from "dayjs";
import { ChannelkartNetworkGet, ChannelkartNetworkPost, ChannelkartNetworkPut } from "./networkService";
import { getMetaDataConfig } from "./uiConfigService";
import moment from "moment";
import axios from "axios";
import { BulkOrderMapType } from "../features/secondaryOrder/BulkConfirm";
import { getConfigData } from "./configurationService";
import { EnvironmentRegistry } from "./registry/environmentRegistry";
import { StockCheckResult } from "../features/secondaryOrder/ManageSecondaryOrderUtils";

export async function getOrderList(startDate: string, endDate: string, type: string, minimalResponse?: boolean, includeOrderDetails: boolean=true) {
  const minimalResponseFilter = minimalResponse ? '&minimalResponse=true' : '';
  const response = await ChannelkartNetworkGet(
    `/v2/orders?size=1000&excludeOrderDetails=${!includeOrderDetails}&type=primary&sort=creationTime:desc&filter=creationTime[gte]:${startDate} and creationTime[lte]:${endDate} and type[in]:[${encodeURIComponent(
      type
    )}] ${minimalResponseFilter}&transformerOut=orderDetailSplitGet`
  );
  return response;
}

export async function getGrnList(startDate: string, endDate: string) {
  const response = await ChannelkartNetworkGet(
    `/grn/fetch?size=1000&sort=creationTime:desc&filter=creationTime[gte]:${startDate} and creationTime[lte]:${endDate}&minimalResponse=false&transformerOut=DMSGrnAPITransformer`
  );
  return response;
}

export async function getOrderListWithType(startDate: string, endDate: string, type: string, status?: string, transformer?: boolean, minimalResponse?: boolean, page?: number, includeOrderDetails: boolean=true) {
  const isEb2bAdmin = await isUserEb2bAdmin();
  const sourceFilter = isEb2bAdmin ? ` and source:eb2b ` : '';
  const retailerAsSupplierConfig = await getConfigData("dms_configuration","retailerAsSupplier");
  const retailerAsSupplier = retailerAsSupplierConfig[0]?.configValue ?? false;
  const retailerAsSupplierFilter = retailerAsSupplier ? `&retailerAsSupplier=true` : ``;

  const statusFilter = status ? ` and status[in]:[${status}]` : ``
  const transformerFilter = transformer ?  ` &transformerOut=orderDetailSplitGet` : ``
  const minimalResponseFilter = minimalResponse ? '&minimalResponse=true' : '';
  const response = await ChannelkartNetworkGet(
    `/v2/orders?size=1000&excludeOrderDetails=${!includeOrderDetails}&sort=creationTime:desc&filter=creationTime[gte]:${startDate} and creationTime[lte]:${endDate} and type[in]:[${encodeURIComponent(
      type
    )}]${sourceFilter}${statusFilter}${transformerFilter}${minimalResponseFilter}${retailerAsSupplierFilter}&page=${page}`
  );
  return response;
}

const isUserEb2bAdmin = async(): Promise<boolean> => {
  const authContext = JSON.parse(localStorage.authContext);
  const currentUser = authContext.user;
  const currentDesignation: string[] = currentUser?.designation || [];
  const useEb2bSourceFilterInOrderGetForDesignations = await getConfigData("dms_configuration", "addEb2bSourceFilterInOrderGetForDesignations");
  const addEb2bSourceFilterInOrderGetForDesignations = useEb2bSourceFilterInOrderGetForDesignations?.[0]?.configValue?.split(",") 
    ?? ["eb2b_report", "eb2b_mdm"];

  // Normalize currentDesignation to an array
  const designationList = Array.isArray(currentDesignation)
    ? currentDesignation
    : [currentDesignation];

  // Check if any relevant designation is present
  return addEb2bSourceFilterInOrderGetForDesignations?.some((designation: string) =>
    designationList?.includes(designation)
  );
}

export async function getOrderInvoiceDataWithType(startDate: string, endDate: string, type: string, status?: string, transformer?: boolean, minimalResponse?: boolean, page?: number, includeExcludeOrderInvoice: boolean=true, includeSkuDetailsForStatus: string = "") {
  const isEb2bAdmin = await isUserEb2bAdmin();
  const sourceFilter = isEb2bAdmin ? ` and source:eb2b ` : '';
  const statusFilter = status ? ` and status[in]:[${status}]` : ``
  const transformerFilter = transformer ?  ` &transformerOut=OrderV3Transformer` : ``
  const minimalResponseFilter = minimalResponse ? '&minimalResponse=true' : '';
  const includeSkuDetailsForStatusFilter = includeSkuDetailsForStatus ? `&includeSkuDetailsForStatus=${includeSkuDetailsForStatus}` : "";
  const response = await ChannelkartNetworkGet(
    `/v3/orders?size=1000&excludeOrderDetails=${!includeExcludeOrderInvoice}&includeSalesDetails=${includeExcludeOrderInvoice}&sort=creationTime:desc&filter=creationTime[gte]:${startDate} and creationTime[lte]:${endDate} and type[in]:[${encodeURIComponent(
      type
    )}]${sourceFilter}${statusFilter}${transformerFilter}${minimalResponseFilter}${includeSkuDetailsForStatusFilter}&page=${page}`
  );
  return response;
}

export async function updateApproval(requestBody: any){
  const response = await ChannelkartNetworkPost(`/v1/userAccount/userApproval`, requestBody);
  return response;
}

export async function getSalesData(orderId?: string, startDate?: string, endDate?: string, minimalResponse?: boolean, page?: number, invoiceNumber?:string, excludeSalesDetails: boolean=false) {

  let SALES_DATA_URL = `/v3/sales/dmsInvoice?size=1000&sort=creationTime:desc&includeSalesDetails=${!excludeSalesDetails}&transformerOut=DMSInvoiceAPITransformer${minimalResponse ? '&minimalResponse=true' : ''}`

  if (page !== undefined) {
    SALES_DATA_URL += `&page=${page}`;
  }
  let filters = `status[nin]:[PENDING] and type[nin]:[return] and `
  if(orderId) filters += `orderNumber:${orderId} and `
  if(startDate) filters += `creationTime[gte]:${startDate} and `
  if(invoiceNumber) filters += `invoiceNumber:${invoiceNumber} and `
  if(endDate) filters += `creationTime[lte]:${endDate}`

  const response = await ChannelkartNetworkGet(`${SALES_DATA_URL}${filters ? `&filter=${filters}` : ``}`);
  return response;
}

export async function getSalesDataByInvoiceNumber(invoiceNumber:string) {
  try{
    return (await getSalesData("","","",true,0,invoiceNumber)).data.features[0];
  }catch(e){}
  return {};
}

export async function getBulkOpertionData(bulkId: string | undefined) {
  const newOrderStatusMap: BulkOrderMapType = {
    inProgress: [],
    confirmed: [],
    partiallyConfirmed: [],
    partiallyInvoiced: [],
    rejected: [],
    failed: [],
    invoiced: [],
  };

  const orderInvoiceNumberMap: Map<string, string> = new Map<string, string>();

  try {
    const res = await getBulkConfirmStatus(bulkId);
    if (res && res.data) {
      const currentTime = new Date();

      res.data.forEach((entry: any) => {
        const timestamp = entry?.timestamp;
        const lastUpdatedTime = timestamp ? new Date(Number(timestamp)) : null;
        const noOfOrders = entry?.orders?.length;
        entry?.orders.forEach((order: any) => {
          const orderNumber = order?.orderNumber;
          const invoiceNumber = order?.invoiceNumber;
          const status = order?.status;

          if (!orderNumber) return;

          // Calculate the batch index (0-based)
          const batchIndex = Math.floor(noOfOrders / 100);  // Dividing the index by 100 to get the batch number
          const expirationTimeMinutes = 2 * (batchIndex + 1);  // Every batch of 100 orders adds 2 minutes, starting from 2 mins for the first 100

          // Check if more than 2 minutes for every 100 have passed for this order entry
          let isExpired = false;
          if (lastUpdatedTime) {
            const timeDifferenceMinutes =
              (currentTime.getTime() - lastUpdatedTime.getTime()) / (1000 * 60);
            isExpired = timeDifferenceMinutes >= expirationTimeMinutes;
          }

          switch (status) {
            case 'CONFIRMED':
              newOrderStatusMap.confirmed.push(orderNumber);
              break;
            case 'PARTIALLY CONFIRMED':
              newOrderStatusMap.partiallyConfirmed.push(orderNumber);
              break;
            case 'REJECTED':
              newOrderStatusMap.rejected.push(orderNumber);
              break;
            case 'FAILURE':
              newOrderStatusMap.failed.push(orderNumber);
              break;
            case 'PARTIALLY INVOICED':
              if (!newOrderStatusMap.partiallyInvoiced) {
                newOrderStatusMap.partiallyInvoiced = [];
              }
              newOrderStatusMap.partiallyInvoiced.push(orderNumber);
              if(invoiceNumber) orderInvoiceNumberMap.set(orderNumber, invoiceNumber);
              break;
            case 'INVOICED':
              newOrderStatusMap.invoiced.push(orderNumber);
              if(invoiceNumber) orderInvoiceNumberMap.set(orderNumber, invoiceNumber);
              break;
            default:
              // If timestamp is expired, mark as failed, otherwise in progress
              if (isExpired) {
                newOrderStatusMap.failed.push(orderNumber);
              } else {
                newOrderStatusMap.inProgress.push(orderNumber);
              }
              break;
          }
        });
      });
    }
  } catch (e) {
    console.error(e);
  }

  return { newOrderStatusMap, orderInvoiceNumberMap };
}


export async function getAllOrderDetailsData(id: any) {
  const response = await ChannelkartNetworkGet(`/v1/query/getOrderDetailsById?id=${id}`);
  return response;
}

export async function updateOrderStatus(requestBody: any, id: any, useNewAPI?: boolean) {
  const response = await ChannelkartNetworkPut((useNewAPI && useNewAPI) ? `/v3/orders/updateStatus/${id}` : `/orders/status/${id}`, requestBody);
  return response;
}

export async function generateBulkConfirm(requestBody: string []) {
  const response = await ChannelkartNetworkPut(`/v3/orders/status/batch`, requestBody);
  return response;
}

export async function generateBulkInvoice(requestBody: string []) {
  const response = await ChannelkartNetworkPut(`/v3/sales/status/batch`, requestBody);
  return response;
}

export const getBulkConfirmStatus = async (bulkId: string | undefined) => {
  const response = await ChannelkartNetworkGet(bulkId ? `/v3/orders/status/bulk/${bulkId}` : `/v3/orders/status/bulk`);

  return response
}

export const generateInvoiceIRN = async (invoiceNum: string) => {
  const response = await ChannelkartNetworkGet(`/v1/einvoice/generateIRN?invoiceNumber=${invoiceNum}`);

  return response
}

export const generateEWayBill = async (invoiceNum: string, transporterId: string) => {
  const response = await ChannelkartNetworkGet(`/v1/eWayBill/generateEWayBillByIrn?transporterId=${transporterId}&invoiceNumber=${invoiceNum}`);

  return response
}

export async function updateInvoice(requestBody: any) {
  const response = await ChannelkartNetworkPost(`/v3/sales/dmsInvoice/edit`, requestBody);
  return response;
}

export async function createInvoice(requestBody: any) {
  const response = await ChannelkartNetworkPost(`/v3/sales/dmsInvoice`, requestBody);
  return response;
}

export async function updateInvoiceStatus(requestBody: any) {
  const response = await ChannelkartNetworkPut(`/v3/sales/dmsInvoice`, requestBody);
  return response;
}

export async function updateEInvoiceStatus(requestBody: any) {
  const response = await ChannelkartNetworkPost(`/v1/einvoice/cancelIRN`, requestBody);
  return response;
}

export async function updateEWayBillStatus(requestBody: any) {
  const response = await ChannelkartNetworkPost(`/v1/eWayBill/cancelEWayBill`, requestBody);
  return response;
}

export async function generateInvoicePdf(requestBody: any) {
  const response = await ChannelkartNetworkPost(`/tasks/types/invoicePdf/execute`, requestBody);
  return response;
}

export async function generateBatchInvoicePdf(requestBody: any) {
  const taskNameConfig = await getConfigData("dms_configuration","printViewInvoicePdfTaskName");
  const taskNameValue = taskNameConfig[0]?.configValue ?? "batchInvoicePdf";
  const response = await ChannelkartNetworkPost(`/tasks/types/${taskNameValue}/execute`, requestBody);
  return response;
}

export async function generateBulkInvoicePdfCokeNp(requestBody: any) {
  const response = await ChannelkartNetworkPost(`/tasks/types/bulkInvoiceCokeNp/execute`, requestBody);
  return response;
}

export async function generateInvoicePdfFileKey(taskId: string) {
  const response = await ChannelkartNetworkGet(`/tasks/${taskId}`);
  return response;
}


export async function updateOrder(requestBody: any, orderNumber: any) {
  const response = await ChannelkartNetworkPut(`/orders/${orderNumber}`, requestBody);
  return response;
}

export async function updateGrnStatus(requestBody: any) {
  const response = await ChannelkartNetworkPut(`/grn`, requestBody);
  return response;
}

export async function updateGrnInvoice(requestBody: any) {
  const response = await ChannelkartNetworkPut(`/grn/recalculate`, requestBody);
  return response;
}

export async function getOrderTrackingStages(
  type: string = "orderTrackingStages"
): Promise<Array<any>> {
  const res = await getMetaDataConfig("clientconfig", "order_configuration");
  const obj = res?.domainValues?.find((item: any) => item.name === type) ?? null;
  return obj?.value ?? [];
}

export async function getOrderReportIds() {
  const res = await getMetaDataConfig("clientconfig", "order_configuration");
  const orderReportId =
    res?.domainValues?.find((item: any) => item.name === "orderReportId")?.["value"] ?? null;
  const singleOrderReportId =
    res?.domainValues?.find((item: any) => item.name === "singleOrderReportId")?.["value"] ?? null;
  return { orderReportId: orderReportId, singleOrderReportId: singleOrderReportId };
}

export async function getSingleOrder(orderId: string) {
  const response = await ChannelkartNetworkGet(
    `/v2/orders?filter=id:${orderId}&transformerOut=orderDetailSplitGet`
  );
  return response;
}

export async function getReferenceOrdersOms(orderId: string) {
  const response = await ChannelkartNetworkGet(
    `/v2/orders?filter=referenceOrderNumber:${orderId}&type=primary&transformerOut=orderDetailSplitGet`
  );
  return response;
}

export async function getSinglePrimaryOrder(orderId: string) {
  const response = await ChannelkartNetworkGet(
    `/v2/orders?filter=id:${orderId}&type=primary&transformerOut=orderDetailSplitGet`
  );
  return response;
}

export async function checkIsEWayBilledApi(invoiceNumber: string = "") {
  const response = await ChannelkartNetworkGet(
    `/v1/eWayBill/isEWayBillGenerated?invoiceNumber=${invoiceNumber}`
  );
  return response;
}

export async function getSingleGrnOrder(orderId: string) {
  const response = await axios.get("https://3677g.wiremockapi.cloud/grn/fetch/details");
  return response;
}

export function getUTCDate(date: Dayjs, type: "start" | "end") {
  let d =
    type === "start" ? date.format("YYYY-MM-DD 00:00:00") : date.format("YYYY-MM-DD 23:59:59");
  return moment(d).utc().format("YYYY-MM-DD HH:mm:ss");
}

export function getLocalTimeDate(date: Dayjs, type: "start" | "end") {
  let d =
    type === "start" ? date.format("YYYY-MM-DD 00:00:00") : date.format("YYYY-MM-DD 23:59:59");
  return moment(d).format("YYYY-MM-DD HH:mm:ss");
}

export async function getGrnInfoByInvoice(invoiceNumber: string) {
  const response = await ChannelkartNetworkGet(`/grn/fetch?filter=invoiceNumber:${invoiceNumber}&minimalResponse=false&transformerOut=DMSGrnAPITransformer`);
  return response;
  
}

export async function getAllOutletApprovalData() {
  let page = 0;
  const size = 1000;
  let allFeatures: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const res = await ChannelkartNetworkGet(
      `/approval/hierarchyCheck?queryName=ApprovalRequestOutletHierarchy&page=${page}&size=${size}`
    );

    const features = res?.data?.features || [];
    allFeatures = allFeatures.concat(features);

    if (features.length < size) {
      hasMore = false;
    } else {
      page++;
    }
  }
  return allFeatures; // exactly what would have been res.data.features, but combined
}

export async function getOutletApprovalData(outletId: string){
  if (!outletId) return {}
  const response = await ChannelkartNetworkGet(`/approval/${outletId}`);
  return response
}
 
export async function getOutletApprovalDataByDate(startDate: string, endDate: string){
  const response = await ChannelkartNetworkGet(`/v1/approvalInfo?size=1000&sort=creationTime:desc&filter=creationTime[gte]:${startDate} and creationTime[lte]:${endDate}`);
  return response;
}

export async function getOutletListForApproval(startDate: string, endDate: string, transformerName: string) {
  const response = await ChannelkartNetworkGet(
    `/outletDetails/outletApprovalInfo?transformerout=${transformerName}&filter=creationTime[gte]:${startDate} and creationTime[lte]:${endDate}&sort=creationTime:desc&minimalResponse=true&size=1000`);
  return response;
}

export async function getSkuReplacement(
  skuList: string[],
  size: number = 1000
) {
  const filter = `entityCategory:productreplacement and entityType:product and entityId[in]:[${skuList
    .map((sku) => `${sku}`)
    .join(",")}]`

  let page = 0
  let allFeatures: any[] = []
  let totalPages = 1

  while (page < totalPages) {
    const url = `/v1/entitymapping/dynamic?filter=${encodeURIComponent(
      filter
    )}&page=${page}&size=${size}`

    const response = await ChannelkartNetworkGet(url)

    const features = response?.data?.features ?? []
    totalPages = response?.data?.totalPage ?? 1
    allFeatures = allFeatures.concat(features)

    page += 1
  }

  return allFeatures
}

export async function sendBulkInvoicingAfterManualAllocation(requestBody: any){
  const response = await ChannelkartNetworkPut(`/v3/sales/status/batch/manual`, requestBody);
  return response;
}

export async function getCreditData(outletList: string[]) {
  let CREDIT_DATA_URL = `/creditStatus`;

  // Build filters
  let filters = ``;
  if (outletList?.length > 0) {
    filters += `outletCode[in]:[${outletList.join(",")}]`;
  }

  const response = await ChannelkartNetworkGet(
    `${CREDIT_DATA_URL}${filters ? `?filter=${filters}` : ""}`
  );

  return response;
}

export const getStockValidateResponse = async (orderList: any[]): Promise<any> => {
  const pendingOrders = orderList?.filter(order => order?.status?.toUpperCase() === "PENDING") || [];
  if (pendingOrders.length === 0) return [];

  pendingOrders.sort((a, b) => {
    const dateA = new Date(a?.creationTime);
    const dateB = new Date(b?.creationTime);
    return dateB.getTime() - dateA.getTime();
  });

  const payload = pendingOrders.map(order => {
    const skuDetails = (order?.orderDetails || []).map((orderDetail: any) => ({
      skuCode: orderDetail?.batchCode,
      type: orderDetail?.type?.toUpperCase() === "FREEITEM" ? "foc" : "normal",
      pieceQty: orderDetail?.pieceQuantity,
      caseQty: orderDetail?.caseQuantity,
      otherQty: orderDetail?.otherUnitQuantity,
    }));

    return {
      orderId: order?.orderNumber,
      supplier: order?.supplier,
      skuDetails,
      revertDetails: []
    };
  });

  try {
      return await ChannelkartNetworkPut(`${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/bulkSecondaryOrderUpdate?action=VALIDATE`, payload);
    } catch (error: any) {
    if (error.response.status && (error.response.status === 412 || error.response.status === 424 )) {
      const isBinaryData = error.response.data instanceof ArrayBuffer || error.response.data instanceof Uint8Array;
      const data = isBinaryData
        ? JSON.parse(new TextDecoder().decode(error.response.data))
        : error.response.data; 
      return { status : 424, data: data}
    }
    throw error;
  }
}

export type AggregatedStockData = {
  batchCode: string,
  type: string,
  caseQuantity: number,
  pieceQuantity: number,
  otherUnitQuantity: number
}

type StockNode = {
  requestId: string;
  skuCode: string;
  type: string;
  caseQty?: number | string;
  pieceQty?: number | string;
  otherQty?: number | string;
  updateSummaries?: any[]; // Optional: if you plan to use it later
};

export const aggregateStockSummary = async (stockResponse: any, sortedOrderNumberList: string[], orderNumberTillAggregate?: string): Promise<Map<string, AggregatedStockData>> => {
  const aggregateSkuSummariesMap = new Map<string,AggregatedStockData>();
  const { status, data } = stockResponse;
  if(status !== 412) {
    let unsortedStockResponse: StockNode[] = data?.stockResponses || [];
    const responseMap = new Map(
      unsortedStockResponse.map((item: any) => [item.requestId, item])
    );

    // Sort according to the sortedOrderNumberList
    const sortedStockResponse = sortedOrderNumberList.map(orderNumber => responseMap.get(orderNumber)).filter(Boolean);

    for (const stockNode of sortedStockResponse) {
      if (orderNumberTillAggregate && stockNode.requestId === orderNumberTillAggregate) break;

      for(const stockSkuNode of stockNode?.updateSummaries) {
        const batchCode = stockSkuNode.skuCode;
        const type = stockSkuNode.type;

        const caseQuantity = Number(stockSkuNode.caseQty || 0);
        const pieceQuantity = Number(stockSkuNode.pieceQty || 0);
        const otherUnitQuantity = Number(stockSkuNode.otherQty || 0);

        const mapKey = batchCode + '-' + type;

        if (aggregateSkuSummariesMap.has(mapKey)) {
          const existing = aggregateSkuSummariesMap.get(mapKey)!;
          aggregateSkuSummariesMap.set(mapKey, {
            batchCode,
            type,
            caseQuantity: existing.caseQuantity + caseQuantity,
            pieceQuantity: existing.pieceQuantity + pieceQuantity,
            otherUnitQuantity: existing.otherUnitQuantity + otherUnitQuantity,
          });
        } else {
          aggregateSkuSummariesMap.set(mapKey, {
            batchCode,
            type,
            caseQuantity,
            pieceQuantity,
            otherUnitQuantity,
          });
        }
      }
    }
  }
  return aggregateSkuSummariesMap;
}

export const calculateBulkInvoiceSummaryUsingStockValidate = async (
  orderList: any[],
  stockResponse?: any
): Promise<StockCheckResult> => {
  const bulkSummary: StockCheckResult = {
    confOrders: [],
    parOrders: [],
    canOrders: []
  };

  const res = stockResponse ?? await getStockValidateResponse(orderList);

  if (!res) return bulkSummary;

  const { status, data } = res;

  if (status === 200 || status === 201) {
    // all orders fully allocated
    bulkSummary.confOrders = orderList;
  } else if (status === 206) {
    // partial allocation case
    const fully: any[] = [];
    const partially: any[] = [];
    const rejected: any[] = [];

    for (const stockNode of data?.stockResponses || []) {
      switch (stockNode?.statusCode) {
        case 200:
          fully.push(...orderList?.filter((o) => o?.orderNumber === stockNode?.requestId));
          break;
        case 206:
          partially.push(...orderList?.filter((o) => o?.orderNumber === stockNode?.requestId));
          break;
        case 412:
        case 424:
          rejected.push(...orderList?.filter((o) => o?.orderNumber === stockNode?.requestId));
          break;
      }
    }

    bulkSummary.confOrders = fully;
    bulkSummary.parOrders = partially;
    bulkSummary.canOrders = rejected;
  } else if (status === 412 || status === 424) {
    // all rejected
    bulkSummary.canOrders = orderList
  }

  return bulkSummary;
};
